/**
 * Thin HTTP client that talks to the API_Server and transparently surfaces
 * Go-service errors via the antd-style `message` channel.
 *
 * Contract:
 *   - 2xx + JSON body → return parsed JSON.
 *     If the body is an envelope and the envelope's success flag is falsy
 *     (e.g. `{code: -1, success: false, message: '…'}` — Go_Process shape),
 *     fire `message.error(envelope.message)` so the UI shows the reason
 *     without every call site having to duplicate the check. The envelope
 *     is still returned so callers that want to branch on `success` can.
 *   - 2xx + empty body → return `{}`.
 *   - non-2xx → throw `Error(body || HTTP N)` AND fire `message.error`.
 *
 * Normal (success) responses from Go follow `{code: 0, success: true, ...}`.
 * Any other code/success combo is treated as an error and toasted.
 *
 * Call sites that want to suppress the toast (e.g. a handler that has its
 * own inline error UI) can pass `{silent: true}` in the options bag.
 */

import {message} from '@/components/Toast'

export const AUTH_TOKEN_KEY = 'wechat-desk-auth-token'
export const AUTH_REFRESH_TOKEN_KEY = 'wechat-desk-auth-refresh-token'
export const AUTH_USER_KEY = 'wechat-desk-auth-user'
export const AUTH_EXPIRED_EVENT = 'wechat-desk:auth-expired'

export function getAuthToken(): string | null {
    try { return localStorage.getItem(AUTH_TOKEN_KEY) } catch { return null }
}
export function getAuthRefreshToken(): string | null {
    try { return localStorage.getItem(AUTH_REFRESH_TOKEN_KEY) } catch { return null }
}
export function setAuthToken(token: string) {
    try { localStorage.setItem(AUTH_TOKEN_KEY, token) } catch { /* ignore */ }
}
export function setAuthTokens(token: string, refreshToken: string) {
    try {
        localStorage.setItem(AUTH_TOKEN_KEY, token)
        localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken)
    } catch { /* ignore */ }
}
export function clearAuthToken() {
    try {
        localStorage.removeItem(AUTH_TOKEN_KEY)
        localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY)
        localStorage.removeItem(AUTH_USER_KEY)
    } catch { /* ignore */ }
}
export function getAuthUser(): { id: number; username: string; role: string } | null {
    try { const s = localStorage.getItem(AUTH_USER_KEY); return s ? JSON.parse(s) : null } catch { return null }
}
export function setAuthUser(user: { id: number; username: string; role: string }) {
    try { localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user)) } catch { /* ignore */ }
}

export type ApiRequestOptions = {
    /** When true, do not auto-toast server-reported errors for this call. */
    silent?: boolean
}

/**
 * Shape of the Go_Process envelope. Both upper- and lower-cased fields exist
 * because the Go code is inconsistent; we check every variant the legacy
 * `api-types.ts` used to accept.
 */
type Envelope = {
    code?: number
    Code?: number
    success?: boolean
    Success?: boolean
    message?: string
    Message?: string
}

function isEnvelope(value: unknown): value is Envelope {
    if (!value || typeof value !== 'object') return false
    const record = value as Record<string, unknown>
    return (
        'code' in record ||
        'Code' in record ||
        'success' in record ||
        'Success' in record ||
        'message' in record ||
        'Message' in record
    )
}

function isEnvelopeSuccess(envelope: Envelope): boolean {
    // Explicit success flag wins if it's set.
    if (envelope.success === true || envelope.Success === true) return true
    if (envelope.success === false || envelope.Success === false) return false
    // Fall back to numeric code: 0 = ok (Go convention), anything else = error.
    const code = envelope.code ?? envelope.Code
    if (typeof code === 'number') return code === 0
    // No success signal at all — assume success so we don't false-positive.
    return true
}

function extractMessage(envelope: Envelope): string {
    const top = (envelope.message || envelope.Message || '').trim()
    if (top) return top
    // Top-level message is empty. Some endpoints (notably /messages/send)
    // report per-target outcomes in `results[]` instead, e.g.
    //   {success:false, results:[{target:'wxid_...', success:false, message:'用户可能退出'}]}
    // In that case surface the nested reasons joined so the user sees what
    // actually failed and for which target.
    const record = envelope as unknown as Record<string, unknown>
    const results = record.results
    if (Array.isArray(results)) {
        const reasons: string[] = []
        const seen = new Set<string>()
        for (const entry of results) {
            if (!entry || typeof entry !== 'object') continue
            const row = entry as Record<string, unknown>
            if (row.success === true) continue
            const msg = typeof row.message === 'string' ? row.message.trim() : ''
            if (!msg) continue
            const target = typeof row.target === 'string' ? row.target : ''
            const key = target ? `${target}::${msg}` : msg
            if (seen.has(key)) continue
            seen.add(key)
            reasons.push(target ? `${target}: ${msg}` : msg)
        }
        if (reasons.length) {
            return reasons.length <= 2 ? reasons.join('; ') : `${reasons.slice(0, 2).join('; ')}（共 ${reasons.length} 项失败）`
        }
    }
    return ''
}

/** Well-known reason string the Go_Process embeds in nested result entries
 * when a wxid's session has dropped. The passthrough controller catches the
 * top-level form (`code: -13`); this constant is for envelopes like
 * `/messages/send` that surface the reason only inside `results[]`. */
const SESSION_EXPIRED_REASON = '用户可能退出'

function envelopeContainsSessionExpired(envelope: Envelope): boolean {
    const record = envelope as unknown as Record<string, unknown>
    const results = record.results
    if (!Array.isArray(results)) return false
    for (const entry of results) {
        if (!entry || typeof entry !== 'object') continue
        const row = entry as Record<string, unknown>
        const msg = typeof row.message === 'string' ? row.message : ''
        if (msg.includes(SESSION_EXPIRED_REASON)) return true
    }
    return false
}

/** Event name dispatched on `window` when the Go_Process reports Code 13
 * ("用户可能退出"). App.tsx listens for this to auto-logout the current
 * account and force the user back to the login screen. */
export const SESSION_EXPIRED_EVENT = 'wechat-desk:session-expired'

/** Go_Process well-known error code for "当前 wxid 已退出 / session 已失效". */
const SESSION_EXPIRED_CODE = -13

/**
 * Parse + validate. Returns the decoded body. Fires `message.error` on any
 * envelope that reports failure, unless `silent` is requested. Also detects
 * Code 13 and dispatches a session-expired event so the shell can redirect
 * back to the login panel.
 */
function handleEnvelope<T>(body: T, silent: boolean | undefined, path: string): T {
    if (!isEnvelope(body)) return body
    if (isEnvelopeSuccess(body)) return body

    const code = body.code ?? body.Code
    const isSessionExpired =
        code === SESSION_EXPIRED_CODE || envelopeContainsSessionExpired(body)
    if (isSessionExpired) {
        // Session expired. Always surface this one even when silent was
        // requested — the user can't do anything else until they re-login.
        message.error('当前账号登录已退出，请重新登录')
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
        }
        return body
    }

    if (silent) return body
    const reason = extractMessage(body) || `请求失败（${path}）`
    message.error(reason)
    return body
}

async function parseResponseBody(response: Response): Promise<unknown> {
    const text = await response.text()
    if (!text) return {}
    try {
        return JSON.parse(text)
    } catch {
        // Preserve the original text so the caller can still see what came back.
        return text
    }
}

let refreshRequest: Promise<string | null> | null = null
let refreshFailureReason = ''

async function refreshAccessToken(): Promise<string | null> {
    const refreshToken = getAuthRefreshToken()
    if (!refreshToken) {
        refreshFailureReason = ''
        return null
    }
    if (!refreshRequest) {
        refreshFailureReason = ''
        refreshRequest = fetch('/api/auth/refresh', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({refresh_token: refreshToken}),
        }).then(async (response) => {
            const parsed = await parseResponseBody(response)
            if (!response.ok || !isEnvelope(parsed) || !isEnvelopeSuccess(parsed)) {
                refreshFailureReason = authExpiredMessage(parsed)
                return null
            }
            const token = (parsed as Record<string, unknown>).token
            if (typeof token !== 'string' || !token) return null
            setAuthToken(token)
            return token
        }).catch(() => null).finally(() => {
            refreshRequest = null
        })
    }
    return refreshRequest
}

function authExpiredMessage(parsed: unknown): string {
    if (!isEnvelope(parsed)) return '登录已过期，请重新登录'
    const reason = extractMessage(parsed)
    if (reason.includes('账号已过期') || reason.includes('账号试用期已过期')) return '账号已过期'
    if (reason.includes('账号已被禁用')) return '账号已被禁用'
    return reason || '登录已过期，请重新登录'
}

function dispatchAuthExpired(silent?: boolean, reason = '登录已过期，请重新登录') {
    clearAuthToken()
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
    }
    if (!silent) message.error(reason)
}

async function requestJson<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    path: string,
    body: unknown,
    options: ApiRequestOptions | undefined,
): Promise<T> {
    const hasJsonBody = (method === 'POST' || method === 'PUT' || method === 'PATCH') && body !== undefined
    const headers: Record<string, string> = {}
    if (hasJsonBody) {
        headers['Content-Type'] = 'application/json'
        headers['Accept'] = 'application/json'
    } else if (method === 'GET') {
        headers['Accept'] = 'application/json'
    }
    const token = getAuthToken()
    if (token) {
        headers['Authorization'] = `Bearer ${token}`
    }
    const fetchOptions = {
        method,
        headers,
        body: hasJsonBody ? JSON.stringify(body) : undefined,
        cache: method === 'GET' ? 'no-store' as const : undefined,
    }
    let response: Response
    try {
        response = await fetch(`/api${path}`, fetchOptions)
    } catch (err) {
        // Network / CORS / abort — no envelope to parse, fail loudly.
        const reason = err instanceof Error ? err.message : String(err)
        if (!options?.silent) message.error(`网络异常：${reason}`)
        throw err instanceof Error ? err : new Error(reason)
    }

    let parsed = await parseResponseBody(response)

    if (!response.ok) {
        if (response.status === 401) {
            const reason = authExpiredMessage(parsed)
            const refreshedToken = await refreshAccessToken()
            if (refreshedToken) {
                headers['Authorization'] = `Bearer ${refreshedToken}`
                response = await fetch(`/api${path}`, fetchOptions)
                parsed = await parseResponseBody(response)
                if (response.ok) {
                    return handleEnvelope(parsed as T, options?.silent, path)
                }
            }
            const finalReason = refreshFailureReason || reason
            dispatchAuthExpired(options?.silent, finalReason)
            throw new Error(finalReason)
        }
        // Non-2xx: inspect the body for the session-expired code BEFORE any
        // other handling, since the passthrough controller rewrites upstream
        // `code: -13` → HTTP 502 (gateway-failure semantics). Either branch
        // that matches the session-expired signal must toast + dispatch the
        // event so the UI clears and the login screen comes back.
        if (isEnvelope(parsed)) {
            const code = parsed.code ?? parsed.Code
            const isSessionExpired =
                code === SESSION_EXPIRED_CODE || envelopeContainsSessionExpired(parsed)
            if (isSessionExpired) {
                message.error('当前账号登录已退出，请重新登录')
                if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
                }
                throw new Error('当前账号登录已退出，请重新登录')
            }
        }
        // Non-2xx without the special marker: surface a human-readable
        // message from the body if we can.
        let reason = `HTTP ${response.status}`
        if (isEnvelope(parsed)) {
            const envMsg = extractMessage(parsed)
            if (envMsg) reason = envMsg
        } else if (typeof parsed === 'string' && parsed) {
            reason = parsed
        }
        if (!options?.silent) message.error(reason)
        throw new Error(reason)
    }

    return handleEnvelope(parsed as T, options?.silent, path)
}

export function apiGet<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    return requestJson<T>('GET', path, undefined, options)
}

export async function apiBlob(path: string, options?: ApiRequestOptions): Promise<Blob> {
    const headers: Record<string, string> = {}
    const token = getAuthToken()
    if (token) headers.Authorization = `Bearer ${token}`

    let response = await fetch(`/api${path}`, {headers, cache: 'no-store'})
    if (response.status === 401) {
        const parsed = await parseResponseBody(response)
        const reason = authExpiredMessage(parsed)
        const refreshedToken = await refreshAccessToken()
        if (refreshedToken) {
            headers.Authorization = `Bearer ${refreshedToken}`
            response = await fetch(`/api${path}`, {headers, cache: 'no-store'})
            if (response.ok) return response.blob()
        }
        const finalReason = refreshFailureReason || reason
        dispatchAuthExpired(options?.silent, finalReason)
        throw new Error(finalReason)
    }
    if (!response.ok) {
        const parsed = await parseResponseBody(response)
        const reason = isEnvelope(parsed)
            ? extractMessage(parsed) || `HTTP ${response.status}`
            : typeof parsed === 'string' && parsed
                ? parsed
                : `HTTP ${response.status}`
        if (!options?.silent) message.error(reason)
        throw new Error(reason)
    }
    return response.blob()
}

export function apiPost<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return requestJson<T>('POST', path, body, options)
}

export async function apiPostForm<T>(path: string, body: FormData, options?: ApiRequestOptions): Promise<T> {
    const headers: Record<string, string> = {'Accept': 'application/json'}
    const token = getAuthToken()
    if (token) headers.Authorization = `Bearer ${token}`

    let response: Response
    try {
        response = await fetch(`/api${path}`, {method: 'POST', headers, body})
    } catch (err) {
        const reason = err instanceof Error ? err.message : String(err)
        if (!options?.silent) message.error(`网络异常：${reason}`)
        throw err instanceof Error ? err : new Error(reason)
    }

    const parsed = await parseResponseBody(response)
    if (!response.ok) {
        const reason = isEnvelope(parsed)
            ? extractMessage(parsed) || `HTTP ${response.status}`
            : typeof parsed === 'string' && parsed
                ? parsed
                : `HTTP ${response.status}`
        if (!options?.silent) message.error(reason)
        throw new Error(reason)
    }
    return handleEnvelope(parsed as T, options?.silent, path)
}

export function apiPut<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return requestJson<T>('PUT', path, body, options)
}

export function apiDelete<T>(path: string, options?: ApiRequestOptions): Promise<T> {
    return requestJson<T>('DELETE', path, undefined, options)
}

export function apiPatch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
    return requestJson<T>('PATCH', path, body, options)
}
